import { t } from '@moovly/i18n';
import { Checkbox, Input, ModalFooter, Select } from '@moovly/ui';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RequestStatus } from '../../../config/store';
import useCurrentUser from '../../../hooks/useCurrentUser';
import { addMembers, membersSlice } from '../../../store/members.store';
import { RootState } from '../../../store/rootStore';
import { AddUserProps } from '../types/user';

interface AddUserContainerProps {
  onClose: () => void;
}

const AddUserContainer: React.FC<AddUserContainerProps> = ({ onClose }) => {
  const dispatch = useDispatch<any>();
  const nav = useNavigate();
  const { parentOrganizationId, groupsWithAdminRight } = useCurrentUser();
  const memberCreateStatus = useSelector((state: RootState) => state.members.memberships.request.post.status);
  const existingUsers = useSelector((state: RootState) =>
    state.users.users.data ? Object.values(state.users.users.data).filter(Boolean).map((d) => d.email) : []
  );

  const lastMember = useSelector(
    (state: RootState) =>
      state.members.memberships.data!.memberships[state.members.memberships.data!.memberships.length - 1]
  );

  const [state, setState] = React.useState<AddUserProps>({
    email: '',
    firstName: '',
    lastName: '',
    grantLicense: false,
    groupIds: [groupsWithAdminRight[0].id],
  });
  React.useEffect(() => {
    if (memberCreateStatus === RequestStatus.Success) {
      if (state.grantLicense) {
        nav(`/users/${lastMember.user_id}/subscription`);
      }
      dispatch(membersSlice.actions.resetAddRequest());
      onClose();
    }
  }, [memberCreateStatus, dispatch, nav, onClose, state, lastMember]);
  const handleChange = React.useCallback(
    ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) =>
      setState((st) => ({ ...st, [currentTarget.name]: currentTarget.value })),
    []
  );
  const handleCheckboxChange = React.useCallback(
    ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) =>
      setState((st) => ({ ...st, [currentTarget.name]: currentTarget.checked })),
    []
  );
  const handleAddUser = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dispatch(addMembers({...state, organizationId: parentOrganizationId}));
    },
    [state, dispatch]
  );
  const selectableGroups = React.useMemo(
    () => groupsWithAdminRight.map((gr) => ({ value: gr.id, label: gr.name })),
    [groupsWithAdminRight]
  );
  const handleGroupChange = React.useCallback((values: { value: string }[]) => {
    setState((st) => ({ ...st, groupIds: values.map((v) => v.value) }));
  }, []);
  const isValid = React.useMemo<boolean>(() => {
    return !!(state.firstName.length && state.lastName.length && state.email.length && state.groupIds.length);
  }, [state]);
  const emailExists = React.useMemo(() => existingUsers.includes(state.email), [state.email, existingUsers]);

  return (
    <form onSubmit={handleAddUser}>
      <div className="row">
        <Input
          id="add-user-firstName-input"
          label={t('seatModal.firstName') + '*'}
          onChange={handleChange}
          name="firstName"
          required
          className="col-md-6"
          value={state.firstName}
        />
        <Input
          id="add-user-lastName-input"
          label={t('seatModal.lastName') + '*'}
          required
          onChange={handleChange}
          name="lastName"
          className="col-md-6"
          value={state.lastName}
        />
      </div>
      <Input
        id="add-user-email-input"
        type="email"
        label={t('seatModal.emailAddress') + '*'}
        onChange={handleChange}
        name="email"
        required
        error={emailExists ? t('seatModal.emailExists') : undefined}
        value={state.email}
      />

      {groupsWithAdminRight.length > 1 && (
        <Select
          onChange={handleGroupChange}
          options={selectableGroups}
          label={t('seatModal.addToGroup') + '*'}
          isMulti
          value={selectableGroups.filter((gr) => state.groupIds.includes(gr.value))}
        />
      )}
      <div className="d-flex align-items-center">
        <Checkbox
          label={t('seatModal.checkbox')}
          className="me-2"
          id="add-user-assignLicense-input"
          checked={state.grantLicense}
          name="grantLicense"
          onChange={handleCheckboxChange}
        />
      </div>
      <ModalFooter isSubmit onClose={onClose} confirmDisabled={!isValid} />
    </form>
  );
};

export default AddUserContainer;
